import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { ErrorAlert, SessionListResult, SessionPublic } from '@xdocs/schemas';
import { useAuth } from '../../../auth/AuthProvider.js';
import { useWs } from '../../../ws/WsProvider.js';

export interface SessionRow extends SessionPublic {
  /**
   * Best-effort "is this the browser I'm looking at right now" flag.
   *
   * KNOWN GAP (flagged in this task's WIRING.md): neither `AuthResult`
   * (`packages/schemas/src/auth.dto.ts`) nor `sessionPublicSchema` carries a
   * `currentSessionId` — there is no authoritative signal anywhere in the
   * frozen contract for "which `sessions` row is THIS socket/tab". This
   * approximates it via `navigator.userAgent` string equality, which is
   * imprecise (two sessions from the same browser/device collide; a
   * spoofed/altered UA never matches) — good enough for the F13 "this
   * device" affordance, not a security boundary (revoke is still
   * server-authorized per-session regardless of this flag).
   */
  isCurrent: boolean;
}

export interface UseSessionsResult {
  sessions: SessionRow[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  revoke: (sessionId: string) => Promise<void>;
  isRevoking: boolean;
  revokeAllOthers: () => Promise<void>;
  isRevokingAll: boolean;
}

function currentUserAgent(): string | null {
  return typeof navigator === 'undefined' ? null : navigator.userAgent;
}

/** F13 sessions list + revoke / revoke-all-others. */
export function useSessions(): UseSessionsResult {
  const { userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected' && userId !== null;
  const queryKey = useMemo(() => ['user', userId, 'sessions'] as const, [userId]);

  const query = useQuery<SessionListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'session.list', undefined),
    enabled,
  });

  const invalidate = useCallback(() => void queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  const revokeMutation = useMutation<void, ErrorAlert, string>({
    mutationFn: async (sessionId) => {
      await requestTyped(wsClient, 'session.revoke', { sessionId });
    },
    onSuccess: invalidate,
  });

  const revokeAllMutation = useMutation<void, ErrorAlert, string[]>({
    mutationFn: async (sessionIds) => {
      // Sequential, not Promise.all — small N (session lists are never
      // large) and avoids fanning out unbounded parallel writes for a
      // destructive bulk action (typescript.md async-safety guidance).
      for (const sessionId of sessionIds) {
        await requestTyped(wsClient, 'session.revoke', { sessionId });
      }
    },
    onSuccess: invalidate,
  });

  const ua = currentUserAgent();
  const sessions: SessionRow[] = (query.data?.sessions ?? []).map((session) => ({
    ...session,
    isCurrent: ua !== null && session.userAgent === ua && session.revokedAt === null,
  }));

  return {
    sessions,
    isLoading: query.isLoading,
    isError: query.isError,
    onRetry: () => void query.refetch(),
    revoke: (sessionId) => revokeMutation.mutateAsync(sessionId),
    isRevoking: revokeMutation.isPending,
    revokeAllOthers: () => revokeAllMutation.mutateAsync(sessions.filter((s) => !s.isCurrent).map((s) => s.id)),
    isRevokingAll: revokeAllMutation.isPending,
  };
}
